// src/pages/VibeDataPrivacyPage.jsx - Teen-Friendly Privacy & Data Control
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  Lock, 
  Cloud, 
  Smartphone, 
  CheckCircle,
  AlertCircle,
  Download,
  Upload,
  Trash2,
  Crown,
  Zap,
  Eye,
  EyeOff,
  HardDrive,
  Settings,
  Star,
  Coffee,
  Sparkles,
  Heart,
  Brain,
  X,
} from 'lucide-react';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import { useApp } from '../context/AppContext';

const VibeDataPrivacyPage = () => {
  const { 
    getPreferences, 
    updatePreferences, 
    exportUserData, 
    resetProgress,
    getStorageStats,
  } = useApp();
  
  const [preferences, setPreferences] = useState({
    cloudSync: false,
    analytics: false,
    progressTracking: true,
    dataRetentionPeriod: 'indefinite',
    encryptionLevel: 'maximum',
    vibeMode: true,
  });
  
  const [activeTab, setActiveTab] = useState('overview');
  const [storageStats, setStorageStats] = useState(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [operationStatus, setOperationStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // Mock user subscription status - Cloud sync enabled for beta
  const [userSubscription, setUserSubscription] = useState({
    plan: 'free',
    hasCloudSync: true, // Enabled for beta testing
    hasAdvancedAnalytics: false,
  });

  useEffect(() => {
    loadPreferences();
    loadStorageStats();
  }, []);

  const loadPreferences = async () => {
    try {
      const prefs = await getPreferences();
      setPreferences(prefs);
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const loadStorageStats = async () => {
    try {
      const stats = getStorageStats ? getStorageStats() : {
        totalSize: '1.8 MB',
        itemCount: 32,
        categories: {
          'Vibe Checks': 5,
          'Module Progress': 8,
          'Self-Talk Entries': 12,
          'Future Letters': 3,
          'Achievements': 4,
        },
        encrypted: true,
        lastBackup: preferences.cloudSync ? new Date().toLocaleDateString() : null,
      };
      setStorageStats(stats);
    } catch (error) {
      console.error('Error loading storage stats:', error);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    // Premium check disabled for beta - cloud sync available to all users
    // if ((key === 'cloudSync' && !userSubscription.hasCloudSync) && value === true) {
    //   setShowPricingModal(true);
    //   return;
    // }

    try {
      const newPreferences = { ...preferences, [key]: value };
      setPreferences(newPreferences);
      await updatePreferences(newPreferences);
      
      setOperationStatus({
        type: 'success',
        message: 'Settings updated! ✨',
      });
      
      setTimeout(() => setOperationStatus(null), 3000);
    } catch (error) {
      setOperationStatus({
        type: 'error',
        message: 'Oops! Settings update failed.',
      });
      setTimeout(() => setOperationStatus(null), 3000);
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      const data = await exportUserData();
      
      // Create download
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-inner-voice-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setOperationStatus({
        type: 'success',
        message: 'Data exported successfully! Check your downloads 📁',
      });
      setTimeout(() => setOperationStatus(null), 4000);
    } catch (error) {
      setOperationStatus({
        type: 'error',
        message: 'Export failed. Please try again! 😅',
      });
      setTimeout(() => setOperationStatus(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllData = async () => {
    const confirmed = window.confirm(
      '⚠️ Are you sure you want to delete ALL your data? This cannot be undone and you\'ll lose all your progress!',
    );
    
    if (!confirmed) return;
    
    try {
      setLoading(true);
      await resetProgress(true);
      
      setOperationStatus({
        type: 'success',
        message: 'All data cleared. Fresh start! 🌱',
      });
      
      await loadStorageStats();
      setTimeout(() => setOperationStatus(null), 3000);
    } catch (error) {
      setOperationStatus({
        type: 'error',
        message: 'Failed to clear data. Please try again.',
      });
      setTimeout(() => setOperationStatus(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Your Data at a Glance */}
      <Card className="p-6 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border-purple-500/30">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Brain className="w-6 h-6 text-purple-400" />
          Your Data at a Glance 🔍
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
              <span className="text-slate-300">Storage Location:</span>
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-green-400" />
                <span className="text-green-400 font-medium">Your Device Only</span>
              </div>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
              <span className="text-slate-300">Security Level:</span>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-green-400" />
                <span className="text-green-400 font-medium">Bank-Level 🔒</span>
              </div>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
              <span className="text-slate-300">Your Data Size:</span>
              <span className="text-purple-300 font-medium">{storageStats?.totalSize || 'Loading...'}</span>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
              <span className="text-slate-300">Cloud Backup:</span>
              <div className="flex items-center gap-2">
                {preferences.cloudSync ? (
                  <>
                    <Cloud className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-400 font-medium">Enabled ☁️</span>
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-400 font-medium">Private Mode 👤</span>
                  </>
                )}
              </div>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
              <span className="text-slate-300">Who Can See It:</span>
              <span className="text-green-400 font-medium">Only You! 🫵</span>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
              <span className="text-slate-300">Data Sharing:</span>
              <span className="text-green-400 font-medium">Never 🚫</span>
            </div>
          </div>
        </div>
        
        {/* What's Actually Stored */}
        <div className="mt-6 pt-6 border-t border-purple-500/20">
          <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-yellow-400" />
            What's Actually Stored:
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {storageStats?.categories && Object.entries(storageStats.categories).map(([category, count]) => (
              <div key={category} className="flex justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                <span className="text-slate-300 text-sm">{category}</span>
                <span className="text-purple-400 font-medium text-sm">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Privacy Promise */}
      <Card className="p-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
        <div className="flex items-start gap-4">
          <Shield className="w-8 h-8 text-green-400 mt-1" />
          <div>
            <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              Our Privacy Promise to You 💚
            </h3>
            <div className="space-y-3 text-slate-300">
              <p className="flex items-start gap-3 leading-relaxed">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <span>Your deepest thoughts and feelings stay on YOUR device unless you choose cloud sync</span>
              </p>
              <p className="flex items-start gap-3 leading-relaxed">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <span>Everything is encrypted with the same security banks use (seriously!)</span>
              </p>
              <p className="flex items-start gap-3 leading-relaxed">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <span>We literally cannot read your private journals, assessments, or personal reflections</span>
              </p>
              <p className="flex items-start gap-3 leading-relaxed">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <span>You own 100% of your data and can export or delete it whenever you want</span>
              </p>
              <p className="flex items-start gap-3 leading-relaxed">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <span>No data selling, no creepy tracking, no corporate nonsense</span>
              </p>
            </div>
            
            <div className="mt-4 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <p className="text-green-300 text-sm font-medium flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Your mental health deserves the highest level of privacy protection.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Quick Stats */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Your Privacy Stats 📊</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-purple-500/10 rounded-lg">
            <div className="text-2xl font-bold text-purple-400">{storageStats?.itemCount || 0}</div>
            <div className="text-xs text-slate-400">Items Stored</div>
          </div>
          <div className="text-center p-3 bg-green-500/10 rounded-lg">
            <div className="text-2xl font-bold text-green-400">100%</div>
            <div className="text-xs text-slate-400">Encrypted</div>
          </div>
          <div className="text-center p-3 bg-blue-500/10 rounded-lg">
            <div className="text-2xl font-bold text-blue-400">0</div>
            <div className="text-xs text-slate-400">People With Access</div>
          </div>
          <div className="text-center p-3 bg-yellow-500/10 rounded-lg">
            <div className="text-2xl font-bold text-yellow-400">∞</div>
            <div className="text-xs text-slate-400">Your Control</div>
          </div>
        </div>
      </Card>
    </div>
  );

  const SettingsTab = () => (
    <div className="space-y-6">
      {/* Cloud Sync */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Cloud className="w-6 h-6 text-blue-400" />
            <h3 className="text-xl font-bold text-white">Cloud Sync ☁️</h3>
            {/* Premium badge hidden for beta - cloud sync available to all */}
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
            <div>
              <h4 className="text-white font-medium mb-1">Sync Across Devices</h4>
              <p className="text-slate-400 text-sm">
                Access your journey from phone, tablet, or computer
              </p>
            </div>
            <button
              onClick={() => handlePreferenceChange('cloudSync', !preferences.cloudSync)}
              className={`w-14 h-7 rounded-full transition-all relative ${
                preferences.cloudSync ? 'bg-blue-500 shadow-lg' : 'bg-slate-600'
              }`}
            >
              <motion.div
                className="w-6 h-6 rounded-full bg-white absolute top-0.5 shadow-md"
                animate={{ left: preferences.cloudSync ? '2rem' : '0.25rem' }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            </button>
          </div>
          
          {/* Premium upgrade card hidden for beta - cloud sync available to all users */}
          {false && (
            <Card className="p-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-500/30">
              <div className="flex items-start gap-4">
                <Crown className="w-6 h-6 text-yellow-400 mt-1" />
                <div>
                  <h4 className="text-white font-semibold mb-2">Unlock Cloud Sync</h4>
                  <p className="text-slate-300 text-sm mb-4">
                    Take your Inner Voice journey everywhere with secure cloud backup.
                  </p>
                  <div className="space-y-2 mb-4">
                    {[
                      'End-to-end encrypted storage',
                      'Access from any device',
                      'Automatic backup',
                      'Never lose your progress',
                    ].map((feature, index) => (
                      <p key={index} className="flex items-center gap-2 text-sm text-slate-400">
                        <CheckCircle className="w-3 h-3 text-green-400" />
                        {feature}
                      </p>
                    ))}
                  </div>
                  <Button
                    onClick={() => setShowPricingModal(true)}
                    size="sm"
                    className="bg-gradient-to-r from-purple-500 to-blue-500"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade to Premium
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </Card>

      {/* Privacy Controls */}
      <Card className="p-6">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Eye className="w-6 h-6 text-green-400" />
          Privacy Controls 🛡️
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
            <div>
              <h4 className="text-white font-medium mb-1">Anonymous Usage Analytics</h4>
              <p className="text-slate-400 text-sm">
                Help improve the app with anonymous data (no personal info)
              </p>
            </div>
            <button
              onClick={() => handlePreferenceChange('analytics', !preferences.analytics)}
              className={`w-14 h-7 rounded-full transition-all relative ${
                preferences.analytics ? 'bg-purple-500 shadow-lg' : 'bg-slate-600'
              }`}
            >
              <motion.div
                className="w-6 h-6 rounded-full bg-white absolute top-0.5 shadow-md"
                animate={{ left: preferences.analytics ? '2rem' : '0.25rem' }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            </button>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
            <div>
              <h4 className="text-white font-medium mb-1">Progress Tracking</h4>
              <p className="text-slate-400 text-sm">
                Track your mental health journey and celebrate achievements
              </p>
            </div>
            <button
              onClick={() => handlePreferenceChange('progressTracking', !preferences.progressTracking)}
              className={`w-14 h-7 rounded-full transition-all relative ${
                preferences.progressTracking ? 'bg-green-500 shadow-lg' : 'bg-slate-600'
              }`}
            >
              <motion.div
                className="w-6 h-6 rounded-full bg-white absolute top-0.5 shadow-md"
                animate={{ left: preferences.progressTracking ? '2rem' : '0.25rem' }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            </button>
          </div>
          
          <div className="p-4 bg-white/5 rounded-xl border border-white/10">
            <h4 className="text-white font-medium mb-2">Data Retention</h4>
            <p className="text-slate-400 text-sm mb-3">
              How long should we keep your data?
            </p>
            <select
              value={preferences.dataRetentionPeriod}
              onChange={(e) => handlePreferenceChange('dataRetentionPeriod', e.target.value)}
              className="w-full p-3 bg-slate-800 border border-slate-600 rounded-lg text-white font-medium"
            >
              <option value="indefinite">Keep forever (recommended)</option>
              <option value="2years">2 years</option>
              <option value="1year">1 year</option>
            </select>
          </div>
        </div>
      </Card>
    </div>
  );

  const ManagementTab = () => (
    <div className="space-y-6">
      {/* Export Data */}
      <Card className="p-6 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/30">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Download className="w-6 h-6 text-blue-400" />
          Export Your Data 📦
        </h3>
        
        <p className="text-slate-300 mb-4 leading-relaxed">
          Download everything - your assessments, progress, journal entries, achievements, and more. 
          Perfect for keeping a personal backup or switching devices!
        </p>
        
        <Button
          onClick={handleExportData}
          disabled={loading}
          className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
        >
          <Download className="w-4 h-4 mr-2" />
          {loading ? 'Creating backup...' : 'Download My Data'}
        </Button>
      </Card>

      {/* Import Data */}
      <Card className="p-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Upload className="w-6 h-6 text-green-400" />
          Import Data 📥
        </h3>
        
        <p className="text-slate-300 mb-4 leading-relaxed">
          Restore from a backup file or transfer data from another device. 
          Your existing data will be safely merged with the imported data.
        </p>
        
        <input
          type="file"
          accept=".json"
          onChange={(e) => {
            // Handle file import
            const file = e.target.files[0];
            if (file) {
              // Process the file
              console.log('File selected:', file.name);
            }
          }}
          className="hidden"
          id="import-file"
        />
        
        <Button
          onClick={() => document.getElementById('import-file').click()}
          variant="secondary"
          disabled={loading}
          className="border-green-500/30 text-green-300 hover:bg-green-500/10"
        >
          <Upload className="w-4 h-4 mr-2" />
          Choose Backup File
        </Button>
      </Card>

      {/* Nuclear Option */}
      <Card className="p-6 bg-gradient-to-r from-red-500/10 to-orange-500/10 border-red-500/30">
        <h3 className="text-xl font-bold text-red-300 mb-4 flex items-center gap-2">
          <Trash2 className="w-6 h-6" />
          Nuclear Option 💥
        </h3>
        
        <p className="text-slate-300 mb-4 leading-relaxed">
          Delete everything permanently from this device and cloud (if enabled). 
          <span className="text-red-300 font-semibold"> This is irreversible and will destroy all your progress!</span>
        </p>
        
        <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20 mb-4">
          <p className="text-red-200 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Make sure you've exported your data first if you want to keep it!
          </p>
        </div>
        
        <Button
          onClick={handleClearAllData}
          disabled={loading}
          className="bg-red-600 hover:bg-red-700 border-red-500"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          {loading ? 'Deleting everything...' : 'Delete All My Data'}
        </Button>
      </Card>
    </div>
  );

  // Simple premium modal
  const PricingModal = () => (
    <AnimatePresence>
      {showPricingModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowPricingModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-slate-800 rounded-2xl max-w-md w-full p-6 border border-purple-500/30"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <button
                onClick={() => setShowPricingModal(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
              
              <Crown className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Upgrade to Premium ✨</h3>
              <p className="text-slate-300 mb-6">
                Unlock cloud sync and take your Inner Voice journey everywhere.
              </p>
              
              <div className="space-y-3 mb-6 text-left">
                {[
                  'Secure cloud backup',
                  'Sync across all devices',
                  'Advanced progress analytics',
                  'Premium support',
                  '30-day money-back guarantee',
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-white">$4.99<span className="text-lg text-slate-400">/month</span></div>
                <div className="text-slate-400 text-sm">Less than a coffee per week!</div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowPricingModal(false)}
                  className="flex-1"
                >
                  Maybe Later
                </Button>
                <Button
                  onClick={() => {
                    // Handle upgrade
                    setShowPricingModal(false);
                  }}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-blue-500"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Get Premium
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-32">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Privacy & Data Control 🛡️
            </h1>
            <p className="text-lg text-slate-300">
              Your mental health data is sacred. Here's how we protect it and give you complete control.
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="bg-slate-800/50 rounded-xl p-1 flex gap-1 flex-wrap">
              {[
                { id: 'overview', label: 'Overview', icon: Eye },
                { id: 'settings', label: 'Settings', icon: Settings },
                { id: 'management', label: 'My Data', icon: HardDrive },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <OverviewTab />
              </motion.div>
            )}

            {activeTab === 'settings' && (
              <motion.div
                key="settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <SettingsTab />
              </motion.div>
            )}

            {activeTab === 'management' && (
              <motion.div
                key="management"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <ManagementTab />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Status Messages */}
          {operationStatus && (
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              className={`fixed bottom-6 right-6 p-4 rounded-xl shadow-2xl max-w-sm z-50 ${
                operationStatus.type === 'success' 
                  ? 'bg-green-900/90 text-green-200 border border-green-500/30'
                  : 'bg-red-900/90 text-red-200 border border-red-500/30'
              }`}
            >
              <div className="flex items-center gap-3">
                {operationStatus.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-400" />
                )}
                <span className="font-medium">{operationStatus.message}</span>
              </div>
            </motion.div>
          )}

          <PricingModal />
        </div>
      </div>
    </div>
  );
};

export default VibeDataPrivacyPage;